import { CloudOff, RefreshCw, WifiOff, X } from "lucide-react";
import { type JSX, type ReactNode, useEffect, useRef, useState } from "react";

import { useConnectionHealth } from "@/hooks/data/useConnectionHealth";
import { initializeSocketManager } from "@/redux/slices/configSlice";
import { useAppDispatch } from "@/redux/store";
import { cn } from "@/utils/core/generalUtils";

type IssueKind = "offline" | "failed" | "retrying" | null;

interface VariantConfig {
  /** Primary banner title. */
  title: string;
  /** Tailwind bg-* class for the wrapper background. */
  bg: string;
  /** Tailwind border-* class. */
  border: string;
  /** Tailwind text-* class for the dismiss (X) button at rest. */
  closeColor: string;
  /** Tailwind hover:text-* class for the dismiss button on hover. */
  closeHoverColor: string;
  /** Tailwind text-* class for the action link / reconnect button. */
  linkColor: string;
  /** Tailwind text-* class for secondary copy (e.g. attempt counter). */
  subTextColor: string;
  /** Tailwind bg-* class for the icon badge circle. */
  iconBadgeBg: string;
}

const VARIANT: Record<Exclude<IssueKind, null>, VariantConfig> = {
  offline: {
    title: "No internet connection.",
    bg: "bg-red-50",
    border: "border-red-200",
    closeColor: "text-red-300",
    closeHoverColor: "hover:text-red-500",
    linkColor: "text-red-600",
    subTextColor: "text-red-500",
    iconBadgeBg: "bg-red-100",
  },

  failed: {
    title: "Connection lost. Please reconnect.",
    bg: "bg-orange-50",
    border: "border-orange-200",
    closeColor: "text-orange-300",
    closeHoverColor: "hover:text-orange-500",
    linkColor: "text-orange-600",
    subTextColor: "text-orange-500",
    iconBadgeBg: "bg-orange-100",
  },

  retrying: {
    title: "Reconnecting to the server\u2026",
    bg: "bg-amber-50",
    border: "border-amber-200",
    closeColor: "text-amber-300",
    closeHoverColor: "hover:text-amber-500",
    linkColor: "text-amber-600",
    subTextColor: "text-amber-500",
    iconBadgeBg: "bg-amber-100",
  },
};

interface BannerIconProps {
  kind: Exclude<IssueKind, null>;
  iconBadgeBg: string;
}

const iconMap: Record<Exclude<IssueKind, null>, ReactNode> = {
  offline: <WifiOff className='size-5 text-red-500' strokeWidth={2} />,
  retrying: (
    <RefreshCw className='size-5 animate-spin text-amber-500' strokeWidth={2} />
  ),
  failed: <CloudOff className='size-5 text-orange-500' strokeWidth={2} />,
};

const BannerIcon = (props: BannerIconProps): JSX.Element => {
  const { kind, iconBadgeBg } = props;
  const badgeClass = cn(
    "flex size-9 shrink-0 items-center justify-center rounded-full",
    iconBadgeBg,
  );

  return <span className={badgeClass}>{iconMap[kind]}</span>;
};

const resolveIssueKind = (
  isOnline: boolean,
  isSocketReconnectFailed: boolean,
  _reconnectAttempts: number,
): IssueKind => {
  if (!isOnline) {
    return "offline";
  }

  if (isSocketReconnectFailed) {
    return "failed";
  }

  // Temporarily disabled - don't show reconnecting banner
  // if (reconnectAttempts > 0) {
  //   return "retrying";
  // }

  return null;
};

const ConnectionHealthBanner = (): JSX.Element | null => {
  const dispatch = useAppDispatch();

  const {
    isOnline,
    isSocketConnected,
    isSocketReconnectFailed,
    reconnectAttempts,
  } = useConnectionHealth();

  const issueKind = resolveIssueKind(
    isOnline,
    isSocketReconnectFailed,
    reconnectAttempts,
  );

  const [dismissedFor, setDismissedFor] = useState<IssueKind>(null);
  const prevIssueKindRef = useRef<IssueKind>(null);

  useEffect(() => {
    const prev = prevIssueKindRef.current;
    prevIssueKindRef.current = issueKind;

    if (!issueKind) {
      setDismissedFor(null);

      return;
    }

    if (issueKind !== prev) {
      setDismissedFor(null);
    }
  }, [issueKind]);

  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isSocketConnected) {
      setIsReconnecting(false);
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }
  }, [isSocketConnected]);

  useEffect(() => {
    return (): void => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, []);

  const handleClose = (): void => {
    setDismissedFor(issueKind);
  };

  const handleReconnect = (): void => {
    setIsReconnecting(true);
    dispatch(initializeSocketManager());

    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
    }

    reconnectTimerRef.current = setTimeout(() => {
      setIsReconnecting(false);
      reconnectTimerRef.current = null;
    }, 8_000);
  };

  const isBannerVisible = issueKind !== null && dismissedFor !== issueKind;

  if (!isBannerVisible) {
    return null;
  }

  const variant = VARIANT[issueKind];
  const isRetrying = issueKind === "retrying";
  const showReconnectLink = issueKind === "failed" || issueKind === "offline";

  return (
    <div
      role='status'
      aria-live='polite'
      aria-atomic='true'
      className={cn(
        "fixed bottom-4 right-4 z-[100]",
        "w-96",
        "animate-in slide-in-from-bottom-4 fade-in-0 duration-300",
      )}
    >
      <div
        className={cn(
          "relative rounded-xl border shadow-lg",
          "px-4 py-4 pr-10",
          variant.bg,
          variant.border,
        )}
      >
        <button
          type='button'
          aria-label='Dismiss notification'
          onClick={handleClose}
          className={cn(
            "absolute top-3 right-3",
            "transition-colors duration-150",
            "focus-visible:outline-none",
            variant.closeColor,
            variant.closeHoverColor,
          )}
        >
          <X size={16} />
        </button>

        <div className='flex items-center gap-3'>
          <BannerIcon kind={issueKind} iconBadgeBg={variant.iconBadgeBg} />

          <div className='min-w-0 flex-1'>
            <p className='text-sm font-medium text-gray-900 leading-snug'>
              {variant.title}
            </p>

            {showReconnectLink && (
              <button
                type='button'
                onClick={handleReconnect}
                disabled={isReconnecting}
                className={cn(
                  "mt-1 text-xs font-semibold underline underline-offset-2",
                  "disabled:opacity-60",
                  "focus-visible:outline-none",
                  "transition-opacity duration-150",
                  variant.linkColor,
                )}
              >
                {isReconnecting ? "Reconnecting\u2026" : "Tap to reconnect"}
              </button>
            )}

            {isRetrying && (
              <p className={cn("mt-1 text-xs", variant.subTextColor)}>
                Attempt {reconnectAttempts} of 5
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionHealthBanner;
